import {
  buildTicketCatalogPricingLine,
  listTicketBillingPackages,
  resolveTicketCatalogPackage,
  ticketCatalogBillingShortLabel,
  type TicketCatalogPackage,
} from "@/modules/catalog/ticketCatalogPricing";

export type DeliverableBillingKind =
  | "supplement"
  | "roof"
  | "siding"
  | "esx"
  | "pdf_analysis"
  | (string & {});

export type SupplementPricingInput = {
  itemCount: number;
  hasRoof: boolean;
  hasSiding: boolean;
  hasEsx: boolean;
  hasPdfAnalysis: boolean;
};

export type DeliverableBillingInput = {
  billing_kind?: DeliverableBillingKind | string | null;
  billing_line_count?: number | null;
  service_package_id?: number | string | null;
  /** Flat price for a service line that is not a catalog product. */
  billing_unit_price?: number | null;
  title?: string | null;
};

export type { TicketCatalogPackage };

export type SupplementPricingLine = {
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  lineTotal: number;
};

export type SupplementPricingBreakdown = {
  lines: SupplementPricingLine[];
  subtotal: number;
  transferFee: number;
  total: number;
};

export const DELIVERABLE_BILLING_OPTIONS: {
  kind: DeliverableBillingKind;
  label: string;
  needsLineCount: boolean;
  flatPrice?: number;
  packageId?: number | string;
}[] = [
  { kind: "supplement", label: "Supplement", needsLineCount: true },
  {
    kind: "roof",
    label: "Roof measurements",
    needsLineCount: false,
    flatPrice: 25,
  },
  {
    kind: "siding",
    label: "Siding measurements",
    needsLineCount: false,
    flatPrice: 50,
  },
  { kind: "esx", label: "ESX creation", needsLineCount: false, flatPrice: 40 },
  {
    kind: "pdf_analysis",
    label: "PDF analysis",
    needsLineCount: false,
    flatPrice: 10,
  },
];

export const buildTicketBillingOptions = (packages: TicketCatalogPackage[]) => {
  const catalogOptions = listTicketBillingPackages(packages).map((pkg) => ({
    kind: (pkg.ticket_billing_slug ?? String(pkg.id)) as DeliverableBillingKind,
    label: pkg.name,
    needsLineCount: pkg.ticket_pricing_mode === "supplement_lines",
    flatPrice:
      pkg.ticket_pricing_mode === "supplement_lines"
        ? undefined
        : Number(pkg.suggested_price) || 0,
    packageId: pkg.id,
  }));

  if (catalogOptions.length > 0) {
    return catalogOptions;
  }

  return DELIVERABLE_BILLING_OPTIONS;
};

const BASE_PRICE = 150;
const BASE_INCLUDED_ITEMS = 50;
const EXTRA_ITEM_PRICE = 0.5;
const ROOF_PRICE = 25;
const SIDING_PRICE = 50;
const ESX_PRICE = 40;
const PDF_ANALYSIS_PRICE = 10;
const STRIPE_FIXED_FEE = 0.3;
const STRIPE_PERCENT_FEE = 0.029;

const roundMoney = (value: number) => Math.round(value * 100) / 100;

/** One-off service price. A catalog product or billing kind wins when set. */
export const customServiceUnitPrice = (
  item: DeliverableBillingInput,
): number | null => {
  if (item.service_package_id || item.billing_kind) return null;
  if (item.billing_unit_price == null) return null;
  const price = Number(item.billing_unit_price);
  if (!Number.isFinite(price) || price < 0) return null;
  return roundMoney(price);
};

export const deliverableIsBillable = (item: DeliverableBillingInput) =>
  customServiceUnitPrice(item) != null ||
  Boolean(item.billing_kind) ||
  Boolean(item.service_package_id);

const customServicePricingLine = (
  item: DeliverableBillingInput,
  propertyAddress: string,
): SupplementPricingLine | null => {
  const price = customServiceUnitPrice(item);
  if (price == null) return null;
  const name = item.title?.trim() || "Service";
  return {
    description: `${name} for ${normalizePropertyAddress(propertyAddress)}`,
    quantity: 1,
    unit: "ea",
    unitPrice: price,
    lineTotal: price,
  };
};

export const normalizePropertyAddress = (subject?: string | null) =>
  subject?.trim() || "Property";

export const calculateSupplementTotalForLineCount = (lineCount: number) => {
  const count = Math.max(0, Math.floor(Number(lineCount) || 0));
  const extraItems = Math.max(0, count - BASE_INCLUDED_ITEMS);
  return roundMoney(BASE_PRICE + extraItems * EXTRA_ITEM_PRICE);
};

const flatPriceForKind = (kind: DeliverableBillingKind) => {
  switch (kind) {
    case "roof":
      return ROOF_PRICE;
    case "siding":
      return SIDING_PRICE;
    case "esx":
      return ESX_PRICE;
    case "pdf_analysis":
      return PDF_ANALYSIS_PRICE;
    default:
      return 0;
  }
};

export const deliverableBillingKindLabel = (kind: DeliverableBillingKind) =>
  DELIVERABLE_BILLING_OPTIONS.find((option) => option.kind === kind)?.label ??
  kind;

export const buildDeliverableLineDescription = (
  kind: DeliverableBillingKind,
  propertyAddress: string,
  _lineCount?: number | null,
): string => {
  const address = normalizePropertyAddress(propertyAddress);

  switch (kind) {
    case "supplement":
      return `Supplement for ${address}`;
    case "roof":
      return `Measurement of roof of ${address}`;
    case "siding":
      return `Measurement of siding of ${address}`;
    case "esx":
      return `ESX creation for ${address}`;
    case "pdf_analysis":
      return `PDF analysis for ${address}`;
    default:
      return `Services for ${address}`;
  }
};

export const buildDeliverablePricingLine = (
  kind: DeliverableBillingKind,
  propertyAddress: string,
  lineCount?: number | null,
  catalogPackages: TicketCatalogPackage[] = [],
  servicePackageId?: number | string | null,
): SupplementPricingLine => {
  const catalogLine = buildTicketCatalogPricingLine(
    {
      billing_kind: kind,
      billing_line_count: lineCount,
      service_package_id: servicePackageId,
    },
    propertyAddress,
    catalogPackages,
  );
  if (catalogLine) {
    return catalogLine;
  }

  if (kind === "supplement") {
    const total = calculateSupplementTotalForLineCount(lineCount ?? 0);
    return {
      description: buildDeliverableLineDescription(
        kind,
        propertyAddress,
        lineCount,
      ),
      quantity: 1,
      unit: "ea",
      unitPrice: total,
      lineTotal: total,
    };
  }

  const unitPrice = flatPriceForKind(kind);

  return {
    description: buildDeliverableLineDescription(
      kind,
      propertyAddress,
      lineCount,
    ),
    quantity: 1,
    unit: "ea",
    unitPrice,
    lineTotal: unitPrice,
  };
};

const finalizePricing = (
  lines: SupplementPricingLine[],
): SupplementPricingBreakdown => {
  const subtotal = roundMoney(
    lines.reduce((sum, line) => sum + line.lineTotal, 0),
  );
  const total = roundMoney(
    (subtotal + STRIPE_FIXED_FEE) / (1 - STRIPE_PERCENT_FEE),
  );
  const transferFee = roundMoney(total - subtotal);
  return { lines, subtotal, transferFee, total };
};

export const calculatePricingFromDeliverables = (
  deliverables: DeliverableBillingInput[],
  propertyAddress: string,
  catalogPackages: TicketCatalogPackage[] = [],
): SupplementPricingBreakdown => {
  const lines = deliverables
    .filter((item) => deliverableIsBillable(item))
    .map((item) => {
      const custom = customServicePricingLine(item, propertyAddress);
      if (custom) return custom;
      return buildDeliverablePricingLine(
        (item.billing_kind ?? "") as DeliverableBillingKind,
        propertyAddress,
        item.billing_line_count,
        catalogPackages,
        item.service_package_id,
      );
    });

  return finalizePricing(lines);
};

export const calculatePricingFromTicketLegacy = (
  input: SupplementPricingInput,
  propertyAddress: string,
): SupplementPricingBreakdown => {
  const address = normalizePropertyAddress(propertyAddress);
  const lines: SupplementPricingLine[] = [];
  const itemCount = Math.max(0, Math.floor(Number(input.itemCount) || 0));

  if (itemCount > 0) {
    lines.push(buildDeliverablePricingLine("supplement", address, itemCount));
  }
  if (input.hasRoof) {
    lines.push(buildDeliverablePricingLine("roof", address));
  }
  if (input.hasSiding) {
    lines.push(buildDeliverablePricingLine("siding", address));
  }
  if (input.hasEsx) {
    lines.push(buildDeliverablePricingLine("esx", address));
  }
  if (input.hasPdfAnalysis) {
    lines.push(buildDeliverablePricingLine("pdf_analysis", address));
  }

  return finalizePricing(lines);
};

/** The billing option a deliverable uses (catalog product or built-in kind). */
export const deliverableBillingOption = (
  item: DeliverableBillingInput,
  catalogPackages: TicketCatalogPackage[] = [],
) => {
  const options = buildTicketBillingOptions(catalogPackages);
  return (
    options.find(
      (option) =>
        item.service_package_id != null &&
        option.packageId != null &&
        String(option.packageId) === String(item.service_package_id),
    ) ?? options.find((option) => option.kind === item.billing_kind)
  );
};

/**
 * A per-line product (Supplement) with no line count yet: it cannot be
 * priced, so the invoice waits for it instead of guessing a number.
 */
export const deliverableNeedsLineCount = (
  item: DeliverableBillingInput,
  catalogPackages: TicketCatalogPackage[] = [],
) =>
  customServiceUnitPrice(item) == null &&
  Boolean(deliverableBillingOption(item, catalogPackages)?.needsLineCount) &&
  !(Number(item.billing_line_count) > 0);

export const allDeliverablesHaveBilling = (
  deliverables: DeliverableBillingInput[],
) =>
  deliverables.length > 0 &&
  deliverables.every((item) => deliverableIsBillable(item));

export const calculateTicketPricing = (
  deliverables: DeliverableBillingInput[],
  ticket: {
    billing_item_count?: number | null;
    billing_has_roof?: boolean | null;
    billing_has_siding?: boolean | null;
    billing_has_esx?: boolean | null;
    billing_has_pdf_analysis?: boolean | null;
  },
  propertyAddress: string,
  catalogPackages: TicketCatalogPackage[] = [],
): SupplementPricingBreakdown => {
  if (allDeliverablesHaveBilling(deliverables)) {
    return calculatePricingFromDeliverables(
      deliverables,
      propertyAddress,
      catalogPackages,
    );
  }

  return calculatePricingFromTicketLegacy(
    {
      itemCount: ticket.billing_item_count ?? 0,
      hasRoof: Boolean(ticket.billing_has_roof),
      hasSiding: Boolean(ticket.billing_has_siding),
      hasEsx: Boolean(ticket.billing_has_esx),
      hasPdfAnalysis: Boolean(ticket.billing_has_pdf_analysis),
    },
    propertyAddress,
  );
};

export type CombinedTicketPricingInput = {
  ticket: { id: number | string } & Parameters<
    typeof calculateTicketPricing
  >[1];
  deliverables: DeliverableBillingInput[];
  propertyAddress: string;
};

export const calculateCombinedTicketPricing = (
  tickets: CombinedTicketPricingInput[],
  catalogPackages: TicketCatalogPackage[] = [],
): SupplementPricingBreakdown => {
  const lines = tickets.flatMap((entry) => {
    const pricing = calculateTicketPricing(
      entry.deliverables,
      entry.ticket,
      entry.propertyAddress,
      catalogPackages,
    );
    return pricing.lines.map((line) => ({
      ...line,
      description: `Ticket #${entry.ticket.id}: ${line.description}`,
    }));
  });

  return finalizePricing(lines);
};

/** @deprecated Use calculateTicketPricing */
export const calculateSupplementPricing = (
  input: SupplementPricingInput,
): SupplementPricingBreakdown =>
  calculatePricingFromTicketLegacy(input, "Property");

/** @deprecated Use calculateTicketPricing */
export const supplementPricingFromTicket = (ticket: {
  billing_item_count?: number | null;
  billing_has_roof?: boolean | null;
  billing_has_siding?: boolean | null;
  billing_has_esx?: boolean | null;
  billing_has_pdf_analysis?: boolean | null;
}) =>
  calculatePricingFromTicketLegacy(
    {
      itemCount: ticket.billing_item_count ?? 0,
      hasRoof: Boolean(ticket.billing_has_roof),
      hasSiding: Boolean(ticket.billing_has_siding),
      hasEsx: Boolean(ticket.billing_has_esx),
      hasPdfAnalysis: Boolean(ticket.billing_has_pdf_analysis),
    },
    "Property",
  );

export const formatSupplementMoney = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value);

export const deliverableBillingSummary = (
  deliverable: DeliverableBillingInput,
  propertyAddress: string,
  catalogPackages: TicketCatalogPackage[] = [],
) => {
  const customPrice = customServiceUnitPrice(deliverable);
  if (customPrice != null) {
    const name = deliverable.title?.trim() || "Service";
    return `${name} for ${normalizePropertyAddress(propertyAddress)} · ${formatSupplementMoney(customPrice)}`;
  }

  if (!deliverable.billing_kind && !deliverable.service_package_id) {
    return "Billing not set";
  }

  const pkg = resolveTicketCatalogPackage(deliverable, catalogPackages);
  const line = buildDeliverablePricingLine(
    (deliverable.billing_kind ??
      pkg?.ticket_billing_slug ??
      "") as DeliverableBillingKind,
    propertyAddress,
    deliverable.billing_line_count,
    catalogPackages,
    deliverable.service_package_id,
  );

  if (pkg?.ticket_pricing_mode === "supplement_lines") {
    return `${line.description} · ${deliverable.billing_line_count ?? 0} lines · ${formatSupplementMoney(line.lineTotal)}`;
  }
  return `${line.description} · ${formatSupplementMoney(line.lineTotal)}`;
};

export const deliverableBillingShortLabel = (
  deliverable: DeliverableBillingInput,
  catalogPackages: TicketCatalogPackage[] = [],
) => {
  if (customServiceUnitPrice(deliverable) != null) {
    return deliverable.title?.trim() || "Service";
  }

  if (!deliverable.billing_kind && !deliverable.service_package_id) {
    return "Billing not set";
  }

  if (catalogPackages.length > 0) {
    return ticketCatalogBillingShortLabel(deliverable, catalogPackages);
  }

  if (deliverable.billing_kind === "supplement") {
    return `Supplement · ${deliverable.billing_line_count ?? 0} lines`;
  }
  return deliverableBillingKindLabel(
    deliverable.billing_kind as DeliverableBillingKind,
  );
};

export const deliverableBillingLineTotal = (
  deliverable: DeliverableBillingInput,
  propertyAddress: string,
  catalogPackages: TicketCatalogPackage[] = [],
) => {
  const customPrice = customServiceUnitPrice(deliverable);
  if (customPrice != null) return customPrice;
  if (!deliverable.billing_kind && !deliverable.service_package_id) return null;
  return buildDeliverablePricingLine(
    (deliverable.billing_kind ?? "") as DeliverableBillingKind,
    propertyAddress,
    deliverable.billing_line_count,
    catalogPackages,
    deliverable.service_package_id,
  ).lineTotal;
};
